import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FormikProvider, useFormik } from 'formik'
import { DateTime } from 'luxon'
import * as yup from 'yup'
import moment from 'moment'
import { times } from 'lodash'
import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro'

import {
  UPDATE_FINANCIAL_PROFILE_KEY,
  updateFinancialProfile,
} from '@/actions/financialProfileActions'
import { UPDATE_USER_KEY, updateUser } from '@/actions/userActions'
import { selectErrorsForKeys } from '@/reducers/fetch'
import {
  getCurrentUser,
  getFinancialProfile,
  selectIsCurrentUserScorp,
} from '@/selectors/user.selectors'
import { fetchUserDocuments } from '@/features/user/your-practice/documents/lib/userDocuments.slice'

import { DATE_FORMATS, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { useAnalyticsTrack } from '@/features/amplitude'
import { markUserActionItemCompleteIfExists } from '@/features/user/dashboard/lib/service'
import { PostOnboardingTaskIdents } from '@/features/onboarding/config'
import { BUSINESS_ENTITY_OPTIONS } from '@/constants/onboardingConstants'
import { STATES } from '@/constants/locationConstants'

import { fetchUserDocumentCategoriesIfNeeded } from '@/featuresV1/Admin/UserDocumentCategories/userDocumentCategories.slice'
import ChangeEmailModal from '@/componentsV1/Finances/Accounts/ChangeEmailModal'
import { LabelDescription } from '@/componentsV1/BaseComponents/Input'

import { TextColors, TypographyStyles } from '@/styles'
import { PracticeProfileFormCta } from '../PracticeProfileFormCta'
import { DocumentUploads } from './DocumentUploads'
import {
  FormikDateInput,
  FormikDropdown,
  FormikLocationSearchInput,
  getFieldNames,
  makeDateSchema,
  makeEinSchema,
  makeReqPhoneNumberSchema,
  makeReqStringSchema,
} from '@/base'

import {
  Alert,
  Breadcrumb,
  Card,
  Heading,
  HeadingVariants,
  Icon,
  Text,
  TooltipProvider,
  Form,
} from '@/ui'

export const YourBusiness = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const track = useAnalyticsTrack()
  const user = useReselector(getCurrentUser)
  const fp = useReselector(getFinancialProfile)
  const isScorp = useReselector(selectIsCurrentUserScorp)
  const [changeEmailModalOpen, setChangeEmailModalOpen] = useState(false)
  const errors = useReselector(selectErrorsForKeys, [
    UPDATE_USER_KEY,
    UPDATE_FINANCIAL_PROFILE_KEY(fp?.id),
  ])

  useEffect(() => {
    dispatch(fetchUserDocuments())
    dispatch(fetchUserDocumentCategoriesIfNeeded())
  }, [dispatch])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      businessName: fp?.businessName,
      businessEntity: fp?.businessEntity,
      einNumber: fp?.einNumber,
      yearPracticeStarted: fp?.yearPracticeStarted,
      businessAddress: fp?.businessAddress,
      homeState: fp?.homeState,
      scorpRegistrationState: fp?.scorpRegistrationState,
      entityChangeElectionDate:
        fp?.entityChangeElectionDate &&
        DateTime.fromISO(fp.entityChangeElectionDate, { zone: 'utc' }).toFormat(
          DATE_FORMATS_LUXON.INPUT
        ),
      phoneNumber: user?.phoneNumber,
    },

    onSubmit: async ({
      businessName,
      businessEntity,
      einNumber,
      yearPracticeStarted,
      businessAddress,
      homeState,
      scorpRegistrationState,
      entityChangeElectionDate,
      phoneNumber,
    }) => {
      const fpUpdateRes = await dispatch(
        updateFinancialProfile(fp?.id, {
          businessName: businessName || undefined,
          businessEntity: businessEntity || undefined,
          einNumber: einNumber || undefined,
          yearPracticeStarted: yearPracticeStarted || undefined,
          businessAddress: businessAddress || undefined,
          homeState: homeState || undefined,
          scorpRegistrationState: scorpRegistrationState || undefined,
          entityChangeElectionDate: entityChangeElectionDate || undefined,
        })
      )

      const userUpdateRes = await dispatch(
        updateUser(user?.id, { phoneNumber })
      )

      await markUserActionItemCompleteIfExists(
        PostOnboardingTaskIdents.FILL_OUT_BUSINESS_PROFILE,
        (event, properties) => track(event, properties)
      )

      if (fpUpdateRes && userUpdateRes) {
        navigate('/practice/profile')
      }
    },
  })

  const { submitForm, isSubmitting } = formik
  const fieldNames = getFieldNames(formik)

  // TODO: Fix tooltip and file upload skipcq: JS-0099
  return (
    <div className="flex flex-col gap-2 px-4">
      <Breadcrumb>
        <Breadcrumb.Item>
          <Breadcrumb.Link
            onClick={() => {
              navigate('/practice/profile')
            }}
          >
            <Icon icon={regular('arrow-left')} style={{ marginRight: 8 }} />
            Back to Practice Profile
          </Breadcrumb.Link>
        </Breadcrumb.Item>
      </Breadcrumb>

      <div className="flex flex-row gap-2 items-center">
        <Icon
          icon={solid('suitcase')}
          backgroundStyle="rounded"
          backgroundColor="priority2"
          size="sm"
        />
        <Heading variant={HeadingVariants.H2}>Your Business</Heading>
      </div>

      <Card>
        <TooltipProvider>
          <FormikProvider value={formik}>
            <div className="flex flex-col gap-2">
              {Boolean(errors?.length) && (
                <Alert variant="error">{errors[0].message}</Alert>
              )}

              <Form.Input
                name={fieldNames.businessName}
                label="Business Name"
                schema={makeReqStringSchema({
                  field: 'business name',
                  required: false,
                })}
              />

              <Form.Select
                name={fieldNames.businessEntity}
                label="Business Entity"
                options={BUSINESS_ENTITY_OPTIONS}
                schema={yup.string().nullable().optional()}
              />

              <Form.Input
                name={fieldNames.einNumber}
                label="Business EIN Number"
                format="##-#######"
                componentType="pattern"
                schema={makeEinSchema({ required: false })}
              />

              <FormikDropdown
                label="Year Practice Started"
                placeholder="Select Year"
                name={fieldNames.yearPracticeStarted}
                // Gets a list of years between now and 100 years ago
                optionValues={times(100).map((value) =>
                  (moment().year() - value).toString()
                )}
                fullWidth
                schema={makeReqStringSchema({
                  field: 'year practice started',
                  required: false,
                })}
              />

              <FormikLocationSearchInput
                label="Business Address"
                placeholder="Street address, city, state, zip code"
                name={fieldNames.businessAddress}
                schema={makeReqStringSchema({
                  field: 'business address',
                  required: false,
                })}
              />

              <Form.Select
                label="Home State"
                placeholder="Select state"
                name={fieldNames.homeState}
                // selectOnBlur={false}
                options={STATES}
                schema={makeReqStringSchema({
                  field: 'home state',
                  required: false,
                })}
              />

              {isScorp && (
                <>
                  <Form.Select
                    label="Business Registration State"
                    placeholder="Select state"
                    name={fieldNames.scorpRegistrationState}
                    // selectOnBlur={false}
                    options={STATES}
                    schema={makeReqStringSchema({
                      field: 'business registration state',
                      required: false,
                    })}
                  />

                  <FormikDateInput
                    label="S Corp Election Date"
                    fullWidth
                    name={fieldNames.entityChangeElectionDate}
                    schema={makeDateSchema({ required: false })}
                    format={DATE_FORMATS.INPUT}
                    disabled
                  />
                </>
              )}

              <Form.Input
                name={fieldNames.phoneNumber}
                label="Business Phone"
                componentType="phone"
                schema={makeReqPhoneNumberSchema()}
              />

              <div className="w-full">
                <LabelDescription label="Business Email" />

                <div className="flex items-center">
                  <Text
                    variant={TypographyStyles.BODY_LG}
                    color={TextColors.MUTED}
                  >
                    {user?.email}
                  </Text>

                  <Icon
                    icon={regular('pen-to-square')}
                    onClick={() => setChangeEmailModalOpen(true)}
                    className="ml-16"
                  />
                </div>
              </div>

              <DocumentUploads
                businessEntity={fp?.businessEntity ?? undefined}
                isScorp={isScorp}
              />

              <PracticeProfileFormCta
                submitForm={submitForm}
                isValid
                isSubmitting={isSubmitting}
              />
            </div>
          </FormikProvider>
        </TooltipProvider>
      </Card>

      <ChangeEmailModal
        open={changeEmailModalOpen}
        close={() => setChangeEmailModalOpen(false)}
      />
    </div>
  )
}
